/// <amd-module name="TFS/Admin/Groups/Pivots/Groups/Redux/GroupsPivotSaga" />

import { effects, SagaIterator } from "redux-saga";
import { ActionsOfType } from "redux/Utils/ActionHelper";
import { filterXSS } from "xss";
import { aadPrefix, IOperationStatus, Result } from "TFS/Admin/Common/Constants/Constants";
import { CustomErrorSearchString } from "TFS/Admin/Common/Constants/CustomErrorConstants";
import { DataProviders } from "TFS/Admin/Common/Constants/DataProviderConstants";
import { Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { addGroupMembership, addMembersToGroup, createTeam, listMembership, setAtavar } from "TFS/Admin/Common/DataAccessLayers/GraphDAL";
import { GraphClientName, IGraphRestClient } from "TFS/Admin/Common/RestClient/Graph";
import { convertGraphGroupToIIdentity } from "TFS/Admin/Common/Util/TypeConverters";
import {
    disableMembersLoadingFeatureFlag,
    DisplayMaxMembersSize,
    GroupOperation,
    IEntityType,
    IPermissionPage,
    loadAADMembersFeatureFlag,
    numberOfGroupsPerBatch,
    ProjectScopeUsersFeature,
    ProjectValidUsersWellKnownGroup,
    SearchStatus,
    WellKnownGroupsSids
} from "TFS/Admin/Groups/Common/Constants";
import { IGroupMembers, IGroupsViewData, IGroupMembersViewData, PermissionsGroup } from "TFS/Admin/Groups/Common/Contracts";
import { GroupsPivotActions, GroupsPivotActionTypes } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsPivotActions";
import { GroupsViewActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsViewActions";
import * as AadDataSource from "TFS/Admin/Groups/Common/Redux/DataSource/AadDataSource";
import { updateTeamProperties } from "TFS/Admin/Groups/Common/Redux/DataSource/AdminTeamsDataSource";
import * as GroupsActionDataSource from "TFS/Admin/Groups/Common/Redux/DataSource/GroupsPivotPermissionsDataSource";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { getErrorMessage, IErrorAction } from "TFS/Admin/Groups/Common/Util/ErrorMessageHelper";
import { GroupsComponent, GroupsTelemetryLogger } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { getProjectName, getUrlForGraphSubject } from "TFS/Admin/Groups/Common/Util/LinkPropsHelper";
import { getGroupMembersList, getTopLevelGroups } from "TFS/Admin/Groups/Pivots/Groups/DataSource/GroupsPivotDataSource";
import { mergeAndRemoveDuplicateMembers } from "TFS/Admin/Groups/Pivots/Groups/Util/ArrayUtil";
import { IOrganizationAadData } from "TFS/Admin/OrganizationAadCommon/Contracts/DataModels";
import { caseInsensitiveContains, EmptyGuidString, format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import {
    GraphGroup,
    GraphGroupVstsCreationContext,
    GraphMember,
    GraphMembership,
    GraphSubject,
    GraphSubjectLookupKey,
    GraphSubjectQuery,
    GraphTraversalDirection,
    GraphUser
} from "VSS/Platform/Contracts/Graph";
import { isFeatureEnabled, isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssLocationService } from "VSS/Platform/Location";
import { IIdentity } from "VSSUI/IdentityPicker";
import { MessageCardSeverity } from "VSSUI/MessageCard";

/**
 * Root permissions groups saga.
 */
export function* permissionsGroupsSaga(): SagaIterator {
    // Register the initialize permissions handler
    yield effects.takeEvery(GroupsPivotActionTypes.InitView, fetchTopLevelGroups);
    yield effects.takeEvery(GroupsPivotActionTypes.AddGroupConfirm, onAddGroupConfirm);
    yield effects.takeEvery(GroupsPivotActionTypes.GetGroupPermissions, getGroupPermissions);
    yield effects.takeLatest(GroupsPivotActionTypes.SearchGroups, searchGroups);
    yield effects.takeEvery(GroupsPivotActionTypes.FetchOrgSettings, fetchOrgSettings);
    yield effects.takeEvery(GroupsPivotActionTypes.SetDefaultTeam, setDefaultTeam);
    yield effects.takeEvery(GroupsPivotActionTypes.UpdateMemberInfo, updateMemberInfo);
    yield effects.takeEvery(GroupsPivotActionTypes.OnRenderMissingItem, onRenderMissingItem);
    yield effects.takeEvery(GroupsPivotActionTypes.FetchPermissionsGroups, fetchPermissionsGroups);
}

export function* fetchTopLevelGroups(action: ActionsOfType<GroupsPivotActions, GroupsPivotActionTypes.InitView>): SagaIterator {
    const { isTeamsPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.GroupsPivot,
        "Fetch top level groups",
        isTeamsPage ? Feature.Teams : undefined
    );
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        const redirectToMember: boolean = (yield effects.call(redirectToSubjectPage)) as any;

        if (!redirectToMember) {
            const groups: IGroupsViewData = (yield effects.call(getTopLevelGroups, pageContext, { teamsFlag: isTeamsPage })) as any;

            if (groups) {
                if (groups.isProjectScope && !isTeamsPage) {
                    // we have to preemptively sort the groups by subjectKind for project page
                    groups.identities = groups.identities.sort((a, b) => a.subjectKind.localeCompare(b.subjectKind, navigator.language));
                }

                if (!isFeatureEnabled(pageContext, ProjectScopeUsersFeature, false)) {
                    var projectScopedGroupIndex = groups.identities.findIndex(x => x.sid === WellKnownGroupsSids.ProjectScopedUsers)
                    if (projectScopedGroupIndex >= 0) {
                        groups.identities.splice(projectScopedGroupIndex, 1)
                    }
                }

                yield effects.put(GroupsPivotActions.groupsLoadSucceeded(groups));
                yield effects.put(GroupsPivotActions.setMarkTimeToInteract());

                const pcaGroup = groups.identities.find(x => x.sid === WellKnownGroupsSids.ProjectCollectionAdministrators);
                if (pcaGroup) {
                    yield effects.put(
                        GroupsViewActions.setPcaGroup(
                            pcaGroup,
                            groups.identities.filter(x => x.isWellKnownGroup)
                        )
                    );
                }

                if (isTeamsPage && isFeatureFlagEnabled(pageContext, disableMembersLoadingFeatureFlag, false)) {
                    return;
                } else {
                    yield effects.put(
                        GroupsPivotActions.updateMemberInfo(
                            groups.identities.slice(0, numberOfGroupsPerBatch),
                            false,
                            isTeamsPage ? IPermissionPage.Teams : groups.isProjectScope ? IPermissionPage.Project : undefined
                        )
                    );
                }
            } else {
                yield effects.put(
                    GroupsPivotActions.groupOperationStatus(GroupOperation.FetchGroups, Resources.FetchGroupsFailed, false, MessageCardSeverity.Error)
                );
                yield effects.put(GroupsPivotActions.groupsLoadFailed());
            }
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureDataProviderCall, DataProviders.InitGroupsView, false, error.message || error);
        yield effects.put(
            GroupsPivotActions.groupOperationStatus(GroupOperation.FetchGroups, Resources.FetchGroupsFailed, false, MessageCardSeverity.Error)
        );
        yield effects.put(GroupsPivotActions.groupsLoadFailed());
    }
}

export function* updateMemberInfo(action: ActionsOfType<GroupsPivotActions, GroupsPivotActionTypes.UpdateMemberInfo>): SagaIterator {
    const { groups, filtered, permissionPage } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.GroupsPivot,
        "Fetch group member info",
        permissionPage === IPermissionPage.Teams ? Feature.Teams : undefined
    );
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        const projectName =
            permissionPage != IPermissionPage.Organization ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;

        yield effects.all(
            groups.map(group =>
                effects.call(function* () {
                    if (!group) {
                        return;
                    }
                    const { descriptor } = group;
                    if (!descriptor) {
                        return;
                    }

                    const url = (yield effects.call(
                        getUrlForGraphSubject,
                        descriptor,
                        permissionPage,
                        locationService,
                        telemetryLogger,
                        projectName
                    )) as any;

                    if (isFeatureFlagEnabled(pageContext, loadAADMembersFeatureFlag, false)) {
                        if (!group.members || group.members.length < 0) {
                            let members: IGroupMembersViewData | undefined = undefined;
                            const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
                                pageContext,
                                GroupsComponent.GroupsPivot,
                                "Fetch group member details - data provider",
                                undefined
                            );
                            yield effects.call(telemetryLogger.captureAttemptStart);

                            members = (yield effects.call(getGroupMembersList, pageContext, { subjectDescriptor: descriptor })) as any;
                            const membersCount = members && members.identities ? members.identities.length : group.members ? group.members.length : 0;
                            // update member count
                            yield effects.put(
                                GroupsPivotActions.updateMemberCountAndLinkProps(
                                    {
                                        groupDescriptor: descriptor,
                                        totalMemberCount: membersCount,
                                        linkProps: { href: url }
                                    } as IGroupMembers,
                                    filtered
                                )
                            );

                            if (!members || !members.identities) {
                                yield effects.call(telemetryLogger.captureError, "Tried loading members through data provider: No members found.");
                            } else if (members.identities.length > 0) {
                                const graphMembers: GraphMember[] = members.identities.map(item => item as GraphMember).slice(0, DisplayMaxMembersSize);
                                yield effects.put(
                                    GroupsPivotActions.updateMembers(
                                        { groupDescriptor: descriptor, members: graphMembers } as IGroupMembers,
                                        filtered
                                    )
                                );
                            }
                        }
                    } else {
                        let members: GraphMembership[] | undefined = undefined;
                        let lookupKeys: any[] = [];
                        if (!group.members || group.members.length < 0) {
                            members = (yield effects.call(listMembership, descriptor, telemetryLogger, GraphTraversalDirection.Down)) as any;
                            if (members) {
                                lookupKeys = members.slice(0, DisplayMaxMembersSize).map(x => ({ descriptor: x.memberDescriptor }));
                            }
                        }

                        // update member count
                        yield effects.put(
                            GroupsPivotActions.updateMemberCountAndLinkProps(
                                {
                                    groupDescriptor: descriptor,
                                    totalMemberCount: members ? members.length : group.members ? group.members.length : 0,
                                    linkProps: { href: url }
                                } as IGroupMembers,
                                filtered
                            )
                        );

                        // update member icons
                        if (lookupKeys.length > 0) {
                            yield effects.call(getMembers, descriptor, lookupKeys, filtered, permissionPage);
                        }
                    }
                })
            )
        );
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

export function* onRenderMissingItem(action: ActionsOfType<GroupsPivotActions, GroupsPivotActionTypes.OnRenderMissingItem>): SagaIterator {
    const { groups, numberOfGroupsOnScreen, permissionPage } = action.payload;

    yield effects.put(GroupsPivotActions.updateNumberOfGroupsOnScreen(numberOfGroupsOnScreen + numberOfGroupsPerBatch));
    yield effects.put(GroupsPivotActions.updateMemberInfo(groups.slice(0, numberOfGroupsOnScreen + numberOfGroupsPerBatch), false, permissionPage));
}

function* getMembers(descriptor: string, lookupKeys: GraphSubjectLookupKey[], filtered: boolean, permissionPage?: IPermissionPage): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.GroupsPivot,
        "Fetch group member details",
        permissionPage === IPermissionPage.Teams ? Feature.Teams : undefined
    );
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;

        const graphMembers = (yield effects.call([graphClient, graphClient.lookupSubjects], { lookupKeys })) as any;
        let members: GraphMember[] = [];

        if (graphMembers) {
            members = Object.keys(graphMembers).map(descriptor => graphMembers[descriptor]);
            members = members.filter(x => x);
        }

        yield effects.put(GroupsPivotActions.updateMembers({ groupDescriptor: descriptor, members: members } as IGroupMembers, filtered));
        if (lookupKeys.length > members.length) {
            yield effects.call(telemetryLogger.captureError, format("Members not found: {0}", lookupKeys.length - members.length));
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

function* redirectToSubjectPage(): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupsPivot, "Redirect to member page");
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        const historyService: IVssHistoryService = (yield effects.call([pageContext, pageContext.getService], "IVssHistoryService")) as any;
        const historyState = (yield effects.call([historyService, historyService.getState])) as any;

        let descriptor, teamId;
        if (historyState) {
            descriptor = historyState["subjectDescriptor"];
            teamId = historyState["teamId"];
        }

        if (teamId) {
            descriptor = (yield effects.call(getTeamById, teamId)) as any;
        }

        if (!descriptor) {
            return false;
        }

        const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;
        const result = (yield effects.call([graphClient, graphClient.lookupSubjects], { lookupKeys: [{ descriptor }] })) as any;

        if (!result || !result[descriptor]) {
            return false;
        }

        const graphSubject = result[descriptor];
        graphSubject.descriptor = descriptor;
        if (graphSubject.subjectKind == IEntityType.Group) {
            // Group

            // If user is navigating directly to a team / group, we have to check if the team is the project default team 
            graphSubject.isDefaultTeam = (yield effects.call(updateTeamProperties, pageContext, {
                isDefaultTeam: true,
                teamId: graphSubject.originId
            })) as any;

            const groupModel = graphSubject;

            yield effects.put(GroupsPivotActions.getGroupPermissions(graphSubject as GraphGroup));

            yield effects.put(
                GroupsViewActions.selectedGroupChanged({
                    ...(groupModel as PermissionsGroup),
                    isAadGroup: caseInsensitiveContains(graphSubject.origin, aadPrefix),
                    updateImage: false,
                    isDefaultTeam: graphSubject.isDefaultTeam
                })
            );
        } else {
            // User
            yield effects.put(GroupsViewActions.selectedUserChanged(graphSubject as GraphUser));
        }
        yield effects.put(GroupsViewActions.setURLRedirectFlag());

        return true;
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
        return false;
    }
}

export function* onAddGroupConfirm(action: ActionsOfType<GroupsPivotActions, GroupsPivotActionTypes.AddGroupConfirm>): SagaIterator {
    const {
        displayName,
        description,
        avatar,
        subjectIdentities,
        scopeDescriptor,
        adminsIdentities,
        adminAsMember,
        permissionPage,
        setAreaPath,
        supportServicePrincipals,
        parentGroupIdentity,
    } = action.payload;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.GroupsPivot,
        "Add group",
        permissionPage == IPermissionPage.Teams ? Feature.Teams : undefined
    );
    yield effects.call(telemetryLogger.captureAttemptStart);

    const creationContext: GraphGroupVstsCreationContext = {
        displayName: displayName.trim(),
        description: description,
        storageKey: "",
        crossProject: false,
        descriptor: "",
        restrictedVisibility: false,
        specialGroupType: "Generic"
    }; // TODO: See if there's any way to get this enum generated on the client side to avoid hardcoding a potentially changeable string.
    const operations: IOperationStatus[] = [];

    try {
        const graphClient: IGraphRestClient = (yield effects.call([pageContext, pageContext.getRestClient], GraphClientName)) as any;

        const graphGroup = (yield permissionPage == IPermissionPage.Teams
            ? effects.call(addTeam, description, displayName.trim())
            : effects.call([graphClient, graphClient.createGroup], creationContext, scopeDescriptor)) as any;

        if (permissionPage == IPermissionPage.Teams) {
            const result = (yield effects.call(setParentGroupAndAreaPath, graphGroup, parentGroupIdentity, setAreaPath)) as any;
            operations.push(...result);
        }

        if (avatar.value.length > 0) {
            try {
                yield effects.call(setAtavar, graphGroup.descriptor, avatar, telemetryLogger);
                operations.push({
                    name: Resources.AvatarAction,
                    value: Result.Success,
                    message: ""
                });
            } catch (error) {
                operations.push({
                    name: Resources.AvatarAction,
                    value: Result.Failed,
                    message: error.message
                });
            }
        }

        const identities = adminAsMember ? mergeAndRemoveDuplicateMembers(subjectIdentities, adminsIdentities) : subjectIdentities;
        let resultMembers: any;
        if (identities.length > 0) {
            resultMembers = (yield effects.call(addMembersToGroup, identities, graphGroup, supportServicePrincipals, telemetryLogger, scopeDescriptor)) as any;
        }

        if (permissionPage == IPermissionPage.Teams && adminsIdentities.length > 0) {
            const result = (yield effects.call(addTeamAdmins, graphGroup, adminsIdentities)) as any;
            operations.push(...result);
        }

        let resultMessage = format(
            permissionPage != IPermissionPage.Teams ? Resources.GroupAddSuccess : Resources.CreateTeamSuccess,
            filterXSS(displayName)
        );

        let cardSeverity: MessageCardSeverity = MessageCardSeverity.Info;
        const groupData: PermissionsGroup = graphGroup;
        groupData.membersCount = 0;
        groupData.members = [];
        if (resultMembers) {
            const successCount = resultMembers.success.length;

            if (successCount > 0) {
                const lookupKeys: GraphSubjectLookupKey[] = resultMembers.success
                    .slice(0, DisplayMaxMembersSize)
                    .map((x: any) => ({ descriptor: x.memberDescriptor }));
                const graphMembers = (yield effects.call([graphClient, graphClient.lookupSubjects], { lookupKeys })) as any;
                groupData.members = Object.keys(graphMembers).map(descriptor => graphMembers[descriptor]);
                groupData.membersCount = successCount;
            }
            const failedCount = resultMembers.failed.length;
            if (failedCount > 0) {
                const firstFailed = resultMembers.failed[0].subjectIdentity;
                resultMessage +=
                    failedCount === 1 && !firstFailed.active
                        ? format(Resources.AddMembersToGroupFailedDueToInactiveMember, filterXSS(firstFailed.displayName))
                        : successCount > 0
                            ? format(Resources.AddMembersPartialSuccess, successCount, failedCount)
                            : getErrorMessage(
                                GroupsComponent.GroupMembersPivot, // Intentionally MembersPivot here, since we want same errorMessage parsing for Groups' add member scenario
                                resultMembers.errors.length > 0 ? resultMembers.errors[0] : null,
                                IErrorAction.Add
                            );
                cardSeverity = MessageCardSeverity.Warning;
            } else {
                resultMessage += format(
                    Resources.AddMembersSuccess,
                    successCount,
                    (permissionPage == IPermissionPage.Teams ? Resources.Team : Resources.Group).toLocaleLowerCase()
                );
            }
            operations.push(
                ...resultMembers.success.map((x: any) => ({
                    name: Resources.MembersAction,
                    value: Result.Success,
                    message: x.subjectIdentity.mail ? x.subjectIdentity.mail : x.subjectIdentity.displayName
                }))
            );
            operations.push(
                ...resultMembers.failed.map((x: any, index: number) => ({
                    name: Resources.MembersAction,
                    value: Result.Failed,
                    message: getErrorMessage(GroupsComponent.GroupMembersPivot, resultMembers.errors[index], IErrorAction.Add)
                }))
            );
        }

        const failCount = operations.reduce((n, op) => n + (op.value !== Result.Success ? 1 : 0), 0);
        if (permissionPage == IPermissionPage.Teams && failCount > 0) {
            cardSeverity = MessageCardSeverity.Warning;
            resultMessage = format(Resources.CreatedTeamPartial, filterXSS(displayName), failCount);
        }

        const projectName =
            permissionPage != IPermissionPage.Organization ? yield effects.call(getProjectName, pageContext, telemetryLogger) : undefined;
        const url = (yield effects.call(
            getUrlForGraphSubject,
            groupData.descriptor,
            permissionPage,
            locationService,
            telemetryLogger,
            projectName
        )) as any;
        groupData.linkProps = { href: url };

        yield effects.put(
            GroupsPivotActions.groupOperationStatus(
                GroupOperation.Add,
                resultMessage,
                cardSeverity === MessageCardSeverity.Info,
                cardSeverity,
                groupData,
                permissionPage == IPermissionPage.Teams ? operations : []
            )
        );
    } catch (error) {
        yield effects.put(
            GroupsPivotActions.groupOperationStatus(
                GroupOperation.Add,
                getErrorMessage(GroupsComponent.GroupsPivot, error, IErrorAction.Add),
                false,
                MessageCardSeverity.Error
            )
        );
        yield effects.call(telemetryLogger.captureError, error.message || error);
    } finally {
        yield effects.put(GroupsPivotActions.closeAddGroupPanel());
    }
}

function* addTeam(description: string, displayName: string): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupsPivot, "Add Team", Feature.Teams);

    // Create the team and get it as a GraphGroup object
    return yield effects.call(createTeam, description, displayName, telemetryLogger);
}

function* setParentGroupAndAreaPath(team: GraphGroup, parentGroup: IIdentity, setAreaPath: boolean): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.GroupsPivot,
        "Add Team Parent and AreaPath",
        Feature.Teams
    );

    const result: IOperationStatus[] = [];
    // Add the team as a member of
    if (parentGroup) {
        try {
            yield effects.call(addGroupMembership, pageContext, {
                displayName: parentGroup.displayName,
                subjectDescriptor: parentGroup.subjectDescriptor,
            }, {
                displayName: team.displayName,
                subjectDescriptor: team.descriptor
            }, telemetryLogger);
            result.push({
                name: Resources.PermissionsAction,
                value: Result.Success,
                message: ""
            });
        } catch (error) {
            result.push({
                name: Resources.PermissionsAction,
                value: Result.Failed,
                message: error.message
            });
        }
    }

    const successCode = 204;

    // Create area path
    if (setAreaPath) {
        const setAreaPathResponse: any = (yield effects.call(updateTeamProperties, pageContext, {
            teamDescriptor: team.descriptor,
            setAreaPath: setAreaPath
        })) as any;

        if (
            setAreaPathResponse &&
            setAreaPathResponse.statusDescription &&
            caseInsensitiveContains(setAreaPathResponse.statusDescription, CustomErrorSearchString.NodeDuplicateName)
        ) {
            // controlled error so no need to add telemetry in this case.
            result.push({
                name: Resources.AreaPathAction,
                value: Result.Failed,
                message: Resources.NodeDuplicateNameError
            });
        } else if (!setAreaPathResponse || setAreaPathResponse.statusCode !== successCode) {
            yield effects.call(
                telemetryLogger.captureDataProviderCall,
                DataProviders.SetAreaPath,
                false,
                "Data provider did not return success (204) response"
            );
            result.push({
                name: Resources.AreaPathAction,
                value: Result.Failed,
                message: "Data provider did not return success (204) response"
            });
        } else {
            result.push({
                name: Resources.AreaPathAction,
                value: Result.Success,
                message: ""
            });
        }
    }
    return result;
}

function* addTeamAdmins(createdTeam: GraphGroup, adminsIdentities: IIdentity[]): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupsPivot, "Add Team", Feature.Teams);

    const successCode = 204;

    // Add team admins
    const setTeamAdminsResponse: any = (yield effects.call(updateTeamProperties, pageContext, {
        setTeamAdmins: true,
        teamDescriptor: createdTeam.descriptor,
        admins: adminsIdentities.map(e => e.subjectDescriptor)
    })) as any;

    if (!setTeamAdminsResponse || setTeamAdminsResponse.statusCode !== successCode) {
        yield effects.call(
            telemetryLogger.captureDataProviderCall,
            DataProviders.SetTeamAdmins,
            false,
            "Data provider did not return success (204) response"
        );
        return [
            {
                name: Resources.AdministratorsAction,
                value: Result.Failed,
                details: "Data provider did not return success (204) response"
            }
        ];
    }
    return [
        {
            name: Resources.AdministratorsAction,
            value: Result.Success,
            details: ""
        }
    ];
}

function* getTeamById(teamId: string): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupsPivot, "Get Team", Feature.Teams);

    // get team by ID
    const team: any = (yield effects.call(updateTeamProperties, pageContext, {
        getTeam: true,
        teamId: teamId
    })) as any;

    if (!team) {
        yield effects.call(telemetryLogger.captureDataProviderCall, DataProviders.SetTeamAdmins, false, "Data provider did not return team");
        return;
    }

    return team.identity.subjectDescriptor;
}

export function* getGroupPermissions(action: ActionsOfType<GroupsPivotActions, GroupsPivotActionTypes.GetGroupPermissions>): SagaIterator {
    const graphGroup = action.payload;

    if (!graphGroup) {
        return;
    }

    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.GroupsPivot,
        "Fetch group action permissions"
    );
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        const permissions = (yield effects.call(GroupsActionDataSource.getGroupPermissions, pageContext, graphGroup.descriptor)) as any;

        if (permissions) {
            yield effects.put(GroupsViewActions.getSelectedGroupPermissionsSucceeded(graphGroup.descriptor, permissions));
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

export function* searchGroups(action: ActionsOfType<GroupsPivotActions, GroupsPivotActionTypes.SearchGroups>) {
    const { groups, newKeyword, oldKeyword, scopeDescriptor, permissionPage } = action.payload;

    if (!newKeyword) {
        // Reset view
        yield effects.put(GroupsPivotActions.updateSearchStatus(SearchStatus.None));
    } else if (oldKeyword !== undefined && localeIgnoreCaseComparer(newKeyword, oldKeyword) !== 0) {
        const resultGroups: PermissionsGroup[] = [];

        if (permissionPage == IPermissionPage.Teams) {
            for (const group of groups) {
                if (caseInsensitiveContains(group.displayName, newKeyword)) {
                    resultGroups.push(group);
                }
            }
        } else {
            const pageContext: IVssPageContext = yield effects.getContext("pageContext");
            const graphClient: IGraphRestClient = yield effects.call([pageContext, pageContext.getRestClient], GraphClientName);

            const graphSubjectQuery: GraphSubjectQuery = { query: newKeyword, scopeDescriptor: scopeDescriptor, subjectKind: ["Group"] };

            // Search
            const graphSubjects: GraphSubject[] = yield effects.call([graphClient, graphClient.querySubjects], graphSubjectQuery);

            if (graphSubjects && graphSubjects.length > 0) {
                for (const graphSubject of graphSubjects) {
                    const groupIndex = groups.findIndex(x => x.descriptor === graphSubject.descriptor);
                    resultGroups.push({
                        ...(graphSubject as PermissionsGroup),
                        isAadGroup: graphSubject.origin === aadPrefix,
                        updateImage: false,
                        isDefaultTeam: false,
                        members: groupIndex > -1 ? groups[groupIndex].members : undefined,
                        membersCount: groupIndex > -1 ? groups[groupIndex].membersCount : undefined
                    });
                }
            }
        }

        yield effects.put(GroupsPivotActions.updateSearchResult(newKeyword, resultGroups, false));
        yield effects.put(GroupsPivotActions.updateMemberInfo(resultGroups, true, permissionPage));
    } else {
        // Search did not change
        yield effects.put(GroupsPivotActions.updateSearchStatus(SearchStatus.Completed));
    }
}

export function* fetchOrgSettings(): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.GroupsPivot,
        "Fetch if the Org is AAD Backed"
    );

    yield effects.call(telemetryLogger.captureAttemptStart);
    try {
        const settingsData: IOrganizationAadData = (yield effects.call(AadDataSource.isAadOrg, pageContext)) as any;
        yield effects.put(GroupsPivotActions.fetchOrgSettingsCompleted(settingsData));
    } catch (error) {
        yield effects.call(telemetryLogger.captureError, error.message || error);
    }
}

export function* setDefaultTeam(action: ActionsOfType<GroupsPivotActions, GroupsPivotActionTypes.SetDefaultTeam>): SagaIterator {
    const { teamId } = action.payload;

    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(
        pageContext,
        GroupsComponent.GroupsPivot,
        "Set Default Team",
        Feature.Teams
    );
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        const setDefaultTeamResponse: any = (yield effects.call(updateTeamProperties, pageContext, { setDefaultTeam: true, teamId: teamId })) as any;
        const successCode = 204;

        if (setDefaultTeamResponse && setDefaultTeamResponse.statusCode === successCode) {
            yield effects.put(
                GroupsPivotActions.groupOperationStatus(GroupOperation.SetDefaultTeam, Resources.DefaultTeamUpdate, true, MessageCardSeverity.Info)
            );

            // Don't need to reload the entire data again after this update. Just clear the old default team flag and set the new one.
            // This is being done with the assumption that all the team data is already available on the client.
            // If we end up doing paged loading for the teams pivot, this needs to be updated with the InitView call (and that will incur a server call).
            yield effects.put(GroupsPivotActions.updateDefaultTeamFlag(teamId));
        } else {
            yield effects.call(
                telemetryLogger.captureDataProviderCall,
                DataProviders.SetDefaultTeam,
                false,
                "Data provider did not return success (204) response"
            );
            yield effects.put(
                GroupsPivotActions.groupOperationStatus(
                    GroupOperation.SetDefaultTeam,
                    Resources.SetDefaultTeamFailed,
                    false,
                    MessageCardSeverity.Error
                )
            );
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureDataProviderCall, DataProviders.SetDefaultTeam, false, error.message || error);
        yield effects.put(
            GroupsPivotActions.groupOperationStatus(GroupOperation.SetDefaultTeam, Resources.SetDefaultTeamFailed, false, MessageCardSeverity.Error)
        );
    }
}

export function* fetchPermissionsGroups(action: ActionsOfType<GroupsPivotActions, GroupsPivotActionTypes.FetchPermissionsGroups>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const telemetryLogger: GroupsTelemetryLogger = new GroupsTelemetryLogger(pageContext, GroupsComponent.GroupsPivot, "Fetch Permissions Groups");
    yield effects.call(telemetryLogger.captureAttemptStart);

    try {
        const groups: IGroupsViewData = (yield effects.call(getTopLevelGroups, pageContext, { teamsFlag: false })) as any;

        if (groups) {
            const filteredGroups = groups.identities.filter(
                x => x.subjectKind !== IEntityType.Team && x.displayName !== ProjectValidUsersWellKnownGroup
            );

            const filteredIdentities = [];
            for (const group of filteredGroups) {
                filteredIdentities.push(convertGraphGroupToIIdentity(pageContext, group));
            }

            yield effects.put(GroupsPivotActions.fetchPermissionsGroupsSuccess(filteredIdentities));
        } else {
            yield effects.call(telemetryLogger.captureDataProviderCall, DataProviders.FetchPermissionsGroups, false, "No permissions groups fetched");
        }
    } catch (error) {
        yield effects.call(telemetryLogger.captureDataProviderCall, DataProviders.FetchPermissionsGroups, false, error.message || error);
    }
}
